<template>
  <div class="metrics-picker-item" @click="onClick">
    <div v-if="multiple" class="v-list__tile__action">
      <div class="v-input v-input--selection-controls v-input--checkbox theme--light">
        <div class="v-input__control">
          <div class="v-input__slot">
            <div class="v-input--selection-controls__input">
              <i
                class="metrics-picker-item__unchecked-icon v-icon far fa-square theme--light"
                aria-hidden="true"
              ></i>
              <i
                class="metrics-picker-item__checked-icon v-icon fas fa-check-square theme--light"
                aria-hidden="true"
              ></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="item">
      <slot>
        <span class="v-list-item-title">{{ title }}</span>
        <ui-plan-limits v-if="hasPlanLimits" class="ml-4"></ui-plan-limits>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { usePlanLimits, type PlanLimitsProp } from '@/composables';

type SelectControl = {
  isMenuActive: boolean;
};

const props = withDefaults(
  defineProps<{
    item?: Record<string, unknown> | string;
    itemText?: string;
    itemTitleField?: string;
    multiple?: boolean;
    select?: SelectControl;
  }>(),
  {
    itemText: 'text',
  },
);

const planLimitsControl = usePlanLimits();

const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
  planLimitsControl.hasPlanLimitsRestriction(limitKey);

const title = computed(() => {
  const item = props.item;
  if (typeof item === 'string') return item;
  if (item == null || typeof item !== 'object') return '';

  const titleField = props.itemTitleField;
  const textField = props.itemText;

  if (titleField && textField && titleField !== textField) {
    const labelTitle = item[titleField];
    const labelText = item[textField];
    const titleStr = labelTitle != null && String(labelTitle).length > 0 ? String(labelTitle) : '';
    const textStr = labelText != null && String(labelText).length > 0 ? String(labelText) : '';
    if (titleStr && textStr) {
      return titleStr !== textStr ? `${titleStr} — ${textStr}` : textStr;
    }
    return textStr || titleStr;
  }

  const fromItemTextKey = item[textField as keyof typeof item];
  if (fromItemTextKey != null && String(fromItemTextKey).length > 0) {
    return String(fromItemTextKey);
  }

  const listTitle = (item as { title?: unknown }).title;
  if (listTitle != null && String(listTitle).length > 0) {
    return String(listTitle);
  }

  return '';
});

const hasPlanLimits = computed(() =>
  hasPlanLimitsRestriction((props.item as { planLimits?: PlanLimitsProp })?.planLimits as PlanLimitsProp),
);

const onClick = (event: MouseEvent) => {
  const item = props.item as { click?: (event: MouseEvent) => void; planLimits?: PlanLimitsProp };

  if (item?.click) {
    item.click(event);
    event.stopPropagation();
  }

  if (hasPlanLimits.value && item?.planLimits) {
    planLimitsControl.handleClickPlanLimits(item.planLimits);
    if (props.select) {
      props.select.isMenuActive = false;
    }
    event.stopPropagation();
  }
};
</script>

<style lang="scss" scoped>
.item {
  width: 100%;
  display: flex;
  justify-content: space-between;
}
</style>

<style lang="scss">
.metrics-picker-item {
  display: flex;
  width: 100%;
  height: 32px;
  align-items: center;

  &__content {
    padding-right: 8px;

    &--single {
      padding-left: 8px;
    }
  }

  .v-list__tile__action,
  .v-list-item__prepend {
    min-width: 24px;
    flex-shrink: 0;
  }

  .v-input--selection-controls__input {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0;
    width: 24px !important;
    height: 24px !important;
    min-width: 24px;
    min-height: 24px;
    flex-shrink: 0;
  }

  & &__unchecked-icon {
    font-size: 12px;
    display: flex;
    color: var(--color-gray20) !important;
  }

  & &__checked-icon {
    font-size: 12px;
    display: none;
    color: var(--color-green) !important;
  }

  .v-list__tile--active &,
  .v-list-item--active & {
    .metrics-picker-item__unchecked-icon {
      display: none;
    }

    .metrics-picker-item__checked-icon {
      display: flex;
    }
  }
}
</style>
